import React, { memo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown, DropdownItem, SlideUp, Text } from 'common-components'
import CrownIcon from 'assets/svgr-icons/Crown.svg'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import * as Styled from './styled'

export interface IQualityOption {
  value: number
  label: string
  isPremium?: boolean
}

interface IProps {
  options: IQualityOption[]
  selectedValue: number
  onSelect: (value: number) => void
}

interface IContentProps {
  options: IQualityOption[]
  selectedValue: number
  onSelect: (value: number) => void
}

const QualityDropdownContent: React.FC<IContentProps> = ({
  options,
  selectedValue,
  onSelect
}) => (
  <Styled.DropdownListWrapper>
    {options.map(option => (
      <DropdownItem
        key={option.value}
        title={option.label}
        isSelected={option.value === selectedValue}
        onSelect={() => onSelect(option.value)}
        renderRightContent={() =>
          option.isPremium ? (
            <Styled.CrownBadgeIcon
              icon={<CrownIcon />}
              size="small"
              color="goldDark"
            />
          ) : null
        }
      />
    ))}
  </Styled.DropdownListWrapper>
)

const QualityDropdown: React.FC<IProps> = ({
  options,
  selectedValue,
  onSelect
}) => {
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(true)),
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
  )
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const handleSelect = useCallback(
    (value: number) => {
      onSelect(value)
      closeDropdown()
    },
    [onSelect, closeDropdown]
  )

  const selectedOption = options.find(option => option.value === selectedValue)
  const displayValue = selectedOption?.label || ''

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        <QualityDropdownContent
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelect}
        />
      </SlideUp>,
      document.body
    )
  }

  return (
    <Dropdown
      headerContent={
        <Text type="h2" color="secondaryDark">
          {displayValue}
        </Text>
      }
      mainContent={
        <QualityDropdownContent
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelect}
        />
      }
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
    />
  )
}

export default memo(QualityDropdown)
